import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import './AddressAutocomplete.css';

const AddressAutocomplete = ({
    value,
    onChange,
    onAddressSelect,
    placeholder = "Start typing an address...",
    country = null,
    className = "",
    disabled = false
}) => {
    const [suggestions, setSuggestions] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [showSuggestions, setShowSuggestions] = useState(false);
    const [selectedIndex, setSelectedIndex] = useState(-1);
    const [inputValue, setInputValue] = useState(value || '');
    const [error, setError] = useState('');

    const inputRef = useRef(null);
    const suggestionsRef = useRef(null);
    const debounceRef = useRef(null);

    useEffect(() => {
        setInputValue(value || '');
    }, [value]);

    useEffect(() => {
        if (inputValue.length >= 3) {
            // Debounce the API call
            if (debounceRef.current) {
                clearTimeout(debounceRef.current);
            }

            debounceRef.current = setTimeout(() => {
                fetchSuggestions(inputValue);
            }, 300);
        } else {
            setSuggestions([]);
            setShowSuggestions(false);
        }

        return () => {
            if (debounceRef.current) {
                clearTimeout(debounceRef.current);
            }
        };
    }, [inputValue]);

    const fetchSuggestions = async (query) => {
        try {
            setIsLoading(true);
            setError('');

            const params = new URLSearchParams({
                query: query
            });

            if (country) {
                params.append('country', country);
            }

            const response = await axios.get(
                `${process.env.REACT_APP_API_BASE_URL}/locations/autocomplete?${params}`
            );

            setSuggestions(response.data.suggestions || []);
            setShowSuggestions(true);
            setSelectedIndex(-1);
        } catch (error) {
            console.error('Error fetching address suggestions:', error);
            setError('Failed to load address suggestions');
            setSuggestions([]);
        } finally {
            setIsLoading(false);
        }
    };

    const handleInputChange = (e) => {
        const newValue = e.target.value;
        setInputValue(newValue);
        onChange?.(newValue);
        setShowSuggestions(true);
    };

    const handleSuggestionClick = async (suggestion) => {
        try {
            // Get detailed place information
            const params = new URLSearchParams({
                place_id: suggestion.id,
                source: 'google' // or 'nominatim' based on the source
            });

            const response = await axios.get(
                `${process.env.REACT_APP_API_BASE_URL}/locations/place-details?${params}`
            );

            const placeDetails = response.data;

            // Format the address components
            const addressData = {
                address: formatAddress(placeDetails.address_components),
                details: '',
                city: placeDetails.address_components?.locality || '',
                province: placeDetails.address_components?.administrative_area_level_1 || '',
                country: placeDetails.address_components?.country || '',
                postal_code: placeDetails.address_components?.postal_code || '',
                lat: placeDetails.geometry?.location?.lat || null,
                lng: placeDetails.geometry?.location?.lng || null,
                formatted_address: placeDetails.formatted_address
            };

            setInputValue(placeDetails.formatted_address);
            onChange?.(placeDetails.formatted_address);
            onAddressSelect?.(addressData);
            setShowSuggestions(false);
            setSuggestions([]);

        } catch (error) {
            console.error('Error getting place details:', error);
            // Fallback: just use the suggestion description
            setInputValue(suggestion.description);
            onChange?.(suggestion.description);
            setShowSuggestions(false);
            setSuggestions([]);
        }
    };

    const formatAddress = (components) => {
        if (!components) return '';

        const parts = [];
        if (components.street_number) parts.push(components.street_number);
        if (components.route) parts.push(components.route);

        return parts.join(' ');
    };

    const handleKeyDown = (e) => {
        if (!showSuggestions) return;

        switch (e.key) {
            case 'ArrowDown':
                e.preventDefault();
                setSelectedIndex(prev =>
                    prev < suggestions.length - 1 ? prev + 1 : prev
                );
                break;
            case 'ArrowUp':
                e.preventDefault();
                setSelectedIndex(prev => prev > 0 ? prev - 1 : -1);
                break;
            case 'Enter':
                e.preventDefault();
                if (selectedIndex >= 0 && suggestions[selectedIndex]) {
                    handleSuggestionClick(suggestions[selectedIndex]);
                }
                break;
            case 'Escape':
                setShowSuggestions(false);
                setSelectedIndex(-1);
                break;
        }
    };

    const handleInputFocus = () => {
        if (suggestions.length > 0) {
            setShowSuggestions(true);
        }
    };

    const handleInputBlur = () => {
        // Delay hiding suggestions to allow for clicks
        setTimeout(() => {
            setShowSuggestions(false);
            setSelectedIndex(-1);
        }, 200);
    };

    return (
        <div className={`address-autocomplete ${className}`}>
            <div className="input-container">
                <input
                    ref={inputRef}
                    type="text"
                    value={inputValue}
                    onChange={handleInputChange}
                    onKeyDown={handleKeyDown}
                    onFocus={handleInputFocus}
                    onBlur={handleInputBlur}
                    placeholder={placeholder}
                    disabled={disabled}
                    className={`autocomplete-input ${error ? 'error' : ''}`}
                />
                {isLoading && (
                    <div className="loading-indicator">
                        <div className="spinner"></div>
                    </div>
                )}
            </div>

            {error && (
                <div className="error-message">
                    {error}
                </div>
            )}

            {showSuggestions && suggestions.length > 0 && (
                <div ref={suggestionsRef} className="suggestions-container">
                    <ul className="suggestions-list">
                        {suggestions.map((suggestion, index) => (
                            <li
                                key={suggestion.id}
                                className={`suggestion-item ${index === selectedIndex ? 'selected' : ''}`}
                                onClick={() => handleSuggestionClick(suggestion)}
                                onMouseEnter={() => setSelectedIndex(index)}
                            >
                                <div className="suggestion-main">
                                    {suggestion.structured_formatting?.main_text || suggestion.description}
                                </div>
                                {suggestion.structured_formatting?.secondary_text && (
                                    <div className="suggestion-secondary">
                                        {suggestion.structured_formatting.secondary_text}
                                    </div>
                                )}
                            </li>
                        ))}
                    </ul>
                </div>
            )}
        </div>
    );
};

export default AddressAutocomplete;


